import React, { useEffect, useState, useCallback } from 'react';
import SortByAlphabet from '../components/SortByAlphabet/SortByAlphabet';
import Pagination from '../components/Pagination/Pagination';
import { apiGET, apiPOST } from '../utilities/apiHelpers';
import { toast } from 'react-toastify';
import 'tailwindcss/tailwind.css';
import KidneyMedicinesCard from '../components/kidneyMedicines/KidneyMedicinesCard';
import scrollToTop from '../utilities/scrollToTop';
import { useDispatch, useSelector } from 'react-redux';
import { setCartCount } from '../redux/users/users';

const Products = () => {
    const [products, setProducts] = useState([]);
    const [pagination, setPagination] = useState({
        totalProducts: 0,
        totalPages: 0,
        currentPage: 1,
        pageSize: 10,
    });
    const [selectedLetter, setSelectedLetter] = useState('');
    const [loading, setLoading] = useState(false);
    const [stepperProgressCartData, setStepperProgressCartData] = useState([]);
    const [globalConfig, setGlobalConfig] = useState([]);

    const userId = useSelector((state) => state.user?.userData?.id) || '';
    const dispatch = useDispatch();

    // ✅ Fetch user's stepper/cart progress
    const getUserStepperProgress = useCallback(async () => {
        if (!userId) return;
        try {
            const stepperResponse = await apiGET(`/v1/stepper-progress/user-stepper-progress/${userId}`);
            const data = stepperResponse.data?.data;
            setStepperProgressCartData(data);
            dispatch(setCartCount(data?.cartData?.length || 0));
        } catch (error) {
            console.error("Error fetching cart details", error);
        }
    }, [userId, dispatch]);

    // ✅ Fetch products
    const fetchProducts = useCallback(async (sortIndex = '', page = 1) => {
        setLoading(true);
        try {
            const payload = {
                sortIndex,
                limit: pagination.pageSize,
                page,
            };
            const response = await apiPOST("/v1/product/getAllProducts", payload);
            if (response?.data?.status === true) {
                setProducts(response?.data?.data?.product || []);
                const p = response?.data?.data?.pagination || {};
                setPagination({
                    totalProducts: p.totalProducts || 0,
                    totalPages: p.totalPages || 0,
                    currentPage: p.currentPage || 1,
                    pageSize: p.pageSize || 10,
                });
            } else {
                toast.error(response?.data?.data?.msg || "Failed to fetch products");
            }
        } catch (error) {
            toast.error('Failed to fetch products');
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [pagination.pageSize]);

    // ✅ Fetch global config
    const fetchGlobalConfig = useCallback(async () => {
        try {
            const response = await apiGET(`/v1/global-config/get-config`);
            if (response.status) {
                setGlobalConfig(response?.data?.data?.data[0] || []);
            } else {
                toast.error('Error fetching global config');
            }
        } catch (error) {
            toast.error('Error fetching global config');
            console.error(error);
        }
    }, []);

    // 🔹 Initial fetch
    useEffect(() => {
        scrollToTop();
        fetchProducts(selectedLetter);
        fetchGlobalConfig();
        getUserStepperProgress();
    }, [selectedLetter, fetchProducts, fetchGlobalConfig, getUserStepperProgress]);

    const handleLetterClick = (letter) => {
        setSelectedLetter(letter);
    };

    const handlePageChange = (page) => {
        fetchProducts(selectedLetter, page);
    };

    return (
        <div className="container mx-auto p-4">
            <SortByAlphabet onLetterClick={handleLetterClick} />

            {pagination.totalProducts > 0 && (
                <h2>
                    Showing {pagination.pageSize * (pagination.currentPage - 1) + 1}-
                    {Math.min(pagination.pageSize * pagination.currentPage, pagination.totalProducts)} of {pagination.totalProducts} results
                </h2>
            )}

            {loading ? (
                <div className="flex justify-center items-center">
                    <div className="loader">Loading...</div>
                </div>
            ) : (
                <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-5 gap-1'>
                    {products.length ? (
                        products.map((item) => (
                            <KidneyMedicinesCard
                                key={item.id}
                                item={item}
                                globalConfig={globalConfig}
                                stepperProgressCartData={stepperProgressCartData}
                                setStepperProgressCartData={setStepperProgressCartData}
                            />
                        ))
                    ) : (
                        <div className='col-span-full flex justify-center items-center'>
                            <div className='border border-[#095D7E] py-5 px-20 rounded-lg shadow-md text-center'>
                                Not Found
                            </div>
                        </div>
                    )}
                </div>
            )}

            <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
            />
        </div>
    );
};

export default Products;
